import { useCallback, useRef, useState } from "react";
import { Dayjs } from "dayjs";
import { getSpecialOrderPriceList } from "../../services";
import { SpecialOrderItem, SpecialOrderPriceList } from "../../types/special-order.type";
import { API_DATE_FORMAT } from "./data";

/** What a customer load hands the form once its price list lands; empty for a fresh start. */
export interface Prefill {
  routeUuid?: string;
  items?: SpecialOrderItem[];
}

/** The price list is read for the delivery day: the day the draft is priced on when it is saved. */
const toPriceDay = (day: Dayjs | null): string | undefined =>
  day?.isValid() ? day.format(API_DATE_FORMAT) : undefined;

/**
 * A customer's price list for the delivery day. A customer or a day picked again puts another
 * read in flight and only the newest lands, so a slower earlier answer cannot show the prices
 * of another day. A customer load's prefill waits for whichever read lands last, and that read
 * ends the loading, so a day picked while a load is in flight cannot strand the form.
 */
export function useSpecialOrderPriceList(
  applyPrefill: (priceList: SpecialOrderPriceList, prefill: Prefill) => void,
) {
  const [priceList, setPriceList] = useState<SpecialOrderPriceList | null>(null);
  const [loadingPriceList, setLoadingPriceList] = useState<boolean>(false);
  const latestRequestRef = useRef<number>(0);
  const pendingPrefillRef = useRef<Prefill | null>(null);

  const requestPriceList = useCallback(
    async (customerUuid: string, deliveryDay: Dayjs | null) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;
      try {
        const {
          data: { data },
        } = await getSpecialOrderPriceList(customerUuid, toPriceDay(deliveryDay));
        if (!data || latestRequestRef.current !== requestId) return;
        setPriceList(data);
        const prefill = pendingPrefillRef.current;
        pendingPrefillRef.current = null;
        if (prefill) applyPrefill(data, prefill);
      } finally {
        if (latestRequestRef.current === requestId) setLoadingPriceList(false);
      }
    },
    [applyPrefill],
  );

  /** A customer picked (or an order to edit or copy): the list empties until its prices land. */
  const loadPriceList = useCallback(
    (customerUuid: string, deliveryDay: Dayjs | null, prefill: Prefill = {}) => {
      pendingPrefillRef.current = prefill;
      setLoadingPriceList(true);
      setPriceList(null);
      return requestPriceList(customerUuid, deliveryDay);
    },
    [requestPriceList],
  );

  /**
   * A price period can start or end on any day, so a new delivery day reads the prices again.
   * The products stay the same, so the route and the quantities typed so far are kept.
   */
  const repriceForDay = useCallback(
    async (customerUuid: string, deliveryDay: Dayjs) => {
      try {
        await requestPriceList(customerUuid, deliveryDay);
      } catch {
        // The interceptor shows the failure; the list keeps the prices it had.
      }
    },
    [requestPriceList],
  );

  /** No customer: drops the list and any read still in flight. */
  const clearPriceList = useCallback(() => {
    latestRequestRef.current += 1;
    pendingPrefillRef.current = null;
    setPriceList(null);
    setLoadingPriceList(false);
  }, []);

  return { priceList, loadingPriceList, loadPriceList, repriceForDay, clearPriceList };
}
